import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, XCircle } from 'lucide-react';
import { FileDropzone } from '../onboarding/FileDropzone';
import { useToast } from '../tasks/linear/Toast';
import { ApiError } from '../../lib/api';
import {
  confirmDirectoryImport,
  downloadDirectoryExcel,
  previewDirectoryImport,
  rowLabel,
  type ConfirmResult,
  type DirectoryEntity,
  type PreviewResult,
  type PreviewRow,
  type UnknownCategory,
} from '../../lib/directoryExcelApi';
import { createExpenseCategoriesBulk, type CategoryKind } from '../../lib/expenseCategoriesApi';
import { EXPENSE_CATEGORIES_KEY } from '../../hooks/useExpenseCategories';
import { useCompanies } from '../../hooks/useCompanies';
import { useActiveCompanyId } from '../../store/useCompanyStore';
import { Select } from '../ui/Select';

// Reusable three-step Excel import dialog for the Clients / Suppliers
// directories (CLE-230, backed by CLE-229).
//   1. Upload  — drag/drop or browse an .xlsx (5MB cap), download template.
//   2. Preview — row-level validation table; user keeps/drops rows; confirm.
//   3. Result  — created/skipped counts + per-row failures; refresh the list.

const MAX_BYTES = 5 * 1024 * 1024; // 5MB — matches the backend upload limit.

const ENTITY_LABEL: Record<DirectoryEntity, { singular: string; plural: string; noun: string }> = {
  clients: { singular: 'client', plural: 'clienți', noun: 'Clienți' },
  suppliers: { singular: 'furnizor', plural: 'furnizori', noun: 'Furnizori' },
  subscriptions: { singular: 'abonament', plural: 'abonamente', noun: 'Abonamente' },
  tasks: { singular: 'task', plural: 'task-uri', noun: 'Task-uri' },
  'expense-categories': { singular: 'categorie', plural: 'categorii', noun: 'Categorii' },
};

/** Value of the firm dropdown meaning "shared by every firm" (categories only). */
const SHARED = '__shared__';

type Step = 'upload' | 'preview' | 'result';

interface ExcelImportDialogProps {
  entityType: DirectoryEntity;
  onClose: () => void;
  /**
   * Called after a confirm (also one that created nothing new) so the parent
   * can refetch the list; gets the result (with the import batch id).
   */
  onImportComplete: (result?: ConfirmResult) => void;
  /** Import into this firm (Setări → Onboarding picks it); the picker starts on it. */
  companyId?: string | null;
}

export function ExcelImportDialog({
  entityType,
  onClose,
  onImportComplete,
  companyId: fixedCompanyId,
}: ExcelImportDialogProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const labels = ENTITY_LABEL[entityType];

  // Every import except tasks writes into one firm, picked here (duplicates and
  // category codes are checked in that firm). Categories can also be shared.
  const withCompany = entityType !== 'tasks';
  const isCategories = entityType === 'expense-categories';
  const { data: companies } = useCompanies();
  const activeCompanyId = useActiveCompanyId();
  const [pickedCompany, setPickedCompany] = useState<string | null>(fixedCompanyId ?? null);
  const companyChoice =
    pickedCompany ??
    activeCompanyId ??
    companies?.find((c) => c.isDefault)?.id ??
    companies?.[0]?.id ??
    (isCategories ? SHARED : '');
  /** What the API receives: a firm id, or null for shared / no firm. */
  const companyId = withCompany && companyChoice && companyChoice !== SHARED ? companyChoice : null;
  const companyLabel =
    companyChoice === SHARED
      ? 'Comune tuturor firmelor'
      : (() => {
          const c = companies?.find((x) => x.id === companyChoice);
          return c ? c.displayName || c.legalName : null;
        })();
  const [lastFile, setLastFile] = useState<File | null>(null);
  const [missingOpen, setMissingOpen] = useState(false);

  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [result, setResult] = useState<ConfirmResult | null>(null);

  // Which rows (by rowNumber) the user wants to import. Initialised on preview
  // to every non-error row; the user can toggle individually or in bulk.
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setStep('upload');
    setFileName(null);
    setPreview(null);
    setResult(null);
    setSelected(new Set());
    setParsing(false);
    setImporting(false);
    setError(null);
  }

  async function handleFile(file: File) {
    setError(null);
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setError('Sunt acceptate doar fișiere .xlsx sau .xls.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Fișierul depășește limita de 5MB.');
      return;
    }
    if (withCompany && companies && companies.length > 0 && !companyChoice) {
      setError('Alege firma în care se face importul.');
      return;
    }
    setFileName(file.name);
    setLastFile(file);
    setParsing(true);
    try {
      const res = await previewDirectoryImport(entityType, file, companyId);
      setPreview(res);
      // Default selection: every row except hard errors.
      setSelected(new Set(res.rows.filter((r) => r.status !== 'error').map((r) => r.rowNumber)));
      setStep('preview');
    } catch (err) {
      setError(messageOf(err, 'Fișierul nu a putut fi citit.'));
    } finally {
      setParsing(false);
    }
  }

  const selectableRows = useMemo(
    () => preview?.rows.filter((r) => r.status !== 'error') ?? [],
    [preview],
  );
  const selectedCount = selected.size;
  const allSelectableChecked =
    selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.rowNumber));

  function toggleRow(rowNumber: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(selectableRows.map((r) => r.rowNumber)));
  }

  function selectNone() {
    setSelected(new Set());
  }

  async function handleImport() {
    if (!preview || selectedCount === 0) return;
    setImporting(true);
    setError(null);
    const rows = preview.rows
      .filter((r) => selected.has(r.rowNumber))
      .map((r) => ({ rowNumber: r.rowNumber, data: r.data }));
    try {
      const res = await confirmDirectoryImport(entityType, rows, companyId, fileName);
      setResult(res);
      setStep('result');
      onImportComplete(res);
      if (res.created > 0) {
        toast.show(
          `${res.created} ${res.created === 1 ? labels.singular : labels.plural} ${res.created === 1 ? 'importat' : 'importați'}.`,
        );
      } else {
        toast.show('Nu au fost importate înregistrări noi.');
      }
    } catch (err) {
      setError(messageOf(err, 'Importul a eșuat.'));
    } finally {
      setImporting(false);
    }
  }

  async function handleDownloadTemplate() {
    setError(null);
    try {
      await downloadDirectoryExcel(entityType, companyId);
    } catch (err) {
      setError(messageOf(err, 'Șablonul nu a putut fi descărcat.'));
    }
  }

  /** Category import: the kind is editable per row before confirming. */
  function setRowKind(rowNumber: number, kind: CategoryKind) {
    setPreview((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((r) =>
              r.rowNumber === rowNumber ? { ...r, data: { ...r.data, kind } } : r,
            ),
          }
        : prev,
    );
  }

  /** After "Creează categoriile lipsă": read the same file again. */
  async function handleMissingCreated() {
    setMissingOpen(false);
    void queryClient.invalidateQueries({ queryKey: EXPENSE_CATEGORIES_KEY });
    if (lastFile) await handleFile(lastFile);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-brand-600" />
            <h2 className="text-lg font-semibold text-gray-900">Importă {labels.noun} din Excel</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Închide"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'upload' && withCompany && companies && companies.length > 0 && (
            <div className="mb-4">
              <Select
                label="Firma în care se importă"
                value={companyChoice}
                onChange={(v) => setPickedCompany(v)}
                options={[
                  ...(isCategories ? [{ value: SHARED, label: 'Comune tuturor firmelor' }] : []),
                  ...companies.map((c) => ({ value: c.id, label: c.displayName || c.legalName })),
                ]}
              />
            </div>
          )}

          {step !== 'upload' && withCompany && companyLabel && (
            <p className="mb-3 text-sm text-gray-600">
              Firma: <span className="font-medium text-gray-900">{companyLabel}</span>
            </p>
          )}

          {step === 'upload' && (
            <UploadStep
              entityNoun={labels.noun}
              parsing={parsing}
              fileName={fileName}
              onFile={(f) => void handleFile(f)}
              onDownloadTemplate={() => void handleDownloadTemplate()}
            />
          )}

          {step === 'preview' && preview && (
            <>
              {preview.unknownCategories && preview.unknownCategories.length > 0 && (
                <UnknownCategoriesNotice
                  unknown={preview.unknownCategories}
                  onCreate={() => setMissingOpen(true)}
                />
              )}
              <PreviewStep
                preview={preview}
                selected={selected}
                allSelectableChecked={allSelectableChecked}
                onToggleRow={toggleRow}
                onSelectAll={selectAll}
                onSelectNone={selectNone}
                onKindChange={isCategories ? setRowKind : undefined}
              />
            </>
          )}

          {missingOpen && preview?.unknownCategories && (
            <MissingCategoriesDialog
              unknown={preview.unknownCategories}
              companyId={companyId}
              companyLabel={companyLabel}
              onClose={() => setMissingOpen(false)}
              onCreated={() => void handleMissingCreated()}
            />
          )}

          {step === 'result' && result && <ResultStep result={result} entityNoun={labels.noun} />}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-5 py-3">
          <div className="text-xs text-gray-500">
            {step === 'preview' && preview && (
              <span>
                {selectedCount} din {selectableRows.length} rânduri importabile selectate
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 'preview' && (
              <button
                type="button"
                onClick={reset}
                disabled={importing}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Alege alt fișier
              </button>
            )}
            {step === 'result' ? (
              <>
                <button
                  type="button"
                  onClick={reset}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Importă alt fișier
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
                >
                  Gata
                </button>
              </>
            ) : step === 'preview' ? (
              <button
                type="button"
                onClick={() => void handleImport()}
                disabled={importing || selectedCount === 0}
                className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {importing && (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                )}
                {importing
                  ? 'Se importă…'
                  : `Importă ${selectedCount} ${selectedCount === 1 ? 'înregistrare' : 'înregistrări'}`}
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Anulează
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Upload step ------------------------------------------------------------

function UploadStep({
  entityNoun,
  parsing,
  fileName,
  onFile,
  onDownloadTemplate,
}: {
  entityNoun: string;
  parsing: boolean;
  fileName: string | null;
  onFile: (file: File) => void;
  onDownloadTemplate: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-sm text-gray-600">
          Ai nevoie de format? Descarcă {entityNoun.toLowerCase()} curenți ca șablon, completează
          rânduri noi și încarcă-l înapoi.
        </p>
        <button
          type="button"
          onClick={onDownloadTemplate}
          className="inline-flex items-center gap-2 rounded-md border border-brand-300 bg-white px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
        >
          <Download className="h-4 w-4" />
          Descarcă șablon
        </button>
      </div>

      <FileDropzone
        accept=".xlsx,.xls"
        hint="Doar fișiere .xlsx / .xls, până la 5MB"
        disabled={parsing}
        onFile={onFile}
      />

      {fileName && (
        <p className="text-sm text-gray-600">
          Selectat: <span className="font-medium text-gray-900">{fileName}</span>
        </p>
      )}
      {parsing && (
        <p className="flex items-center gap-2 text-sm text-gray-500">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          Se citește și se validează fișierul…
        </p>
      )}
    </div>
  );
}

// --- Preview step -----------------------------------------------------------

function PreviewStep({
  preview,
  selected,
  allSelectableChecked,
  onToggleRow,
  onSelectAll,
  onSelectNone,
  onKindChange,
}: {
  preview: PreviewResult;
  selected: Set<number>;
  allSelectableChecked: boolean;
  onToggleRow: (rowNumber: number) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  /** Set for the category import: renders an editable "Tip" column. */
  onKindChange?: (rowNumber: number, kind: CategoryKind) => void;
}) {
  const { rows, summary } = preview;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-700">
          <span className="font-semibold text-green-700">{summary.valid} valide</span>
          {', '}
          <span className="font-semibold text-amber-700">{summary.warnings} avertismente</span>
          {', '}
          <span className="font-semibold text-red-700">{summary.errors} erori</span>
          {` (${summary.total} rânduri în total)`}
        </p>
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={allSelectableChecked ? onSelectNone : onSelectAll}
            className="rounded border border-gray-300 px-2 py-1 font-medium text-gray-700 hover:bg-gray-50"
          >
            {allSelectableChecked ? 'Deselectează tot' : 'Selectează tot'}
          </button>
        </div>
      </div>

      {summary.errors > 0 && (
        <p className="flex items-center gap-2 text-xs text-red-600">
          <XCircle className="h-3.5 w-3.5" />
          Rândurile cu erori nu pot fi importate și nu pot fi selectate.
        </p>
      )}

      <div className="max-h-[500px] overflow-auto rounded-md border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
            <tr>
              <th className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  aria-label="Selectează toate rândurile importabile"
                  checked={allSelectableChecked}
                  onChange={allSelectableChecked ? onSelectNone : onSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
              </th>
              <th className="w-12 px-3 py-2">Rând</th>
              <th className="w-12 px-3 py-2">Status</th>
              <th className="px-3 py-2">Nume</th>
              {onKindChange && <th className="px-3 py-2">Tip</th>}
              <th className="px-3 py-2">Probleme</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.map((row) => (
              <PreviewRowItem
                key={row.rowNumber}
                row={row}
                checked={selected.has(row.rowNumber)}
                onToggle={() => onToggleRow(row.rowNumber)}
                onKindChange={onKindChange}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PreviewRowItem({
  row,
  checked,
  onToggle,
  onKindChange,
}: {
  row: PreviewRow;
  checked: boolean;
  onToggle: () => void;
  onKindChange?: (rowNumber: number, kind: CategoryKind) => void;
}) {
  const isError = row.status === 'error';
  return (
    <tr className={isError ? 'bg-red-500/10' : undefined}>
      <td className="px-3 py-2 align-top">
        <input
          type="checkbox"
          aria-label={`Importă rândul ${row.rowNumber}`}
          checked={checked}
          disabled={isError}
          onChange={onToggle}
          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 disabled:opacity-40"
        />
      </td>
      <td className="px-3 py-2 align-top text-gray-500">{row.rowNumber}</td>
      <td className="px-3 py-2 align-top">
        <StatusIcon status={row.status} />
      </td>
      <td className="px-3 py-2 align-top font-medium text-gray-900">
        {rowLabel(row)}
        {onKindChange && typeof row.data.code === 'string' && (
          <span className="ml-2 font-mono text-xs font-normal text-gray-500">{row.data.code}</span>
        )}
      </td>
      {onKindChange && (
        <td className="px-3 py-2 align-top">
          <select
            aria-label={`Tipul categoriei de pe rândul ${row.rowNumber}`}
            value={row.data.kind === 'INCOME' ? 'INCOME' : 'EXPENSE'}
            disabled={isError}
            onChange={(e) => onKindChange(row.rowNumber, e.target.value as CategoryKind)}
            className="rounded border border-gray-300 px-2 py-1 text-xs"
          >
            <option value="EXPENSE">Cheltuială</option>
            <option value="INCOME">Venit</option>
          </select>
        </td>
      )}
      <td className="px-3 py-2 align-top text-xs">
        {row.issues.length === 0 ? (
          <span className="text-gray-400">—</span>
        ) : (
          <ul className="space-y-0.5">
            {row.issues.map((issue, i) => (
              <li key={i} className={isError ? 'text-red-700' : 'text-amber-700'}>
                {issue}
              </li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
}

function StatusIcon({ status }: { status: PreviewRow['status'] }) {
  if (status === 'valid') {
    return <CheckCircle2 className="h-4 w-4 text-green-600" aria-label="Valid" />;
  }
  if (status === 'warning') {
    return <AlertTriangle className="h-4 w-4 text-amber-500" aria-label="Avertisment" />;
  }
  return <XCircle className="h-4 w-4 text-red-600" aria-label="Eroare" />;
}

// --- Unknown expense categories ---------------------------------------------

function UnknownCategoriesNotice({
  unknown,
  onCreate,
}: {
  unknown: UnknownCategory[];
  onCreate: () => void;
}) {
  const rowCount = unknown.reduce((n, u) => n + u.rows.length, 0);
  return (
    <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
      <p className="font-medium">
        {unknown.length === 1
          ? '1 categorie nu există în firmă'
          : `${unknown.length} categorii nu există în firmă`}{' '}
        ({rowCount} {rowCount === 1 ? 'rând' : 'rânduri'} nu se pot importa până nu le creezi):
      </p>
      <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
        {unknown.map((u) => (
          <li key={u.code}>
            <span className="font-mono">{u.code}</span> ({u.rows.length})
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onCreate}
        className="mt-2 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
      >
        Creează categoriile lipsă
      </button>
    </div>
  );
}

function MissingCategoriesDialog({
  unknown,
  companyId,
  companyLabel,
  onClose,
  onCreated,
}: {
  unknown: UnknownCategory[];
  companyId: string | null;
  companyLabel: string | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [items, setItems] = useState(() =>
    unknown.map((u) => ({
      code: u.code,
      name: u.code,
      kind: (u.code.startsWith('venituri') || u.code.startsWith('income')
        ? 'INCOME'
        : 'EXPENSE') as CategoryKind,
    })),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await createExpenseCategoriesBulk(
        companyId,
        items.map((i) => ({ ...i, name: i.name.trim() || i.code })),
      );
      onCreated();
    } catch (err) {
      setError(messageOf(err, 'Categoriile nu au putut fi create.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-xl rounded-lg bg-white p-5 shadow-xl">
        <h3 className="text-base font-semibold text-gray-900">Creează categoriile lipsă</h3>
        <p className="mt-1 text-sm text-gray-600">
          Se creează {companyId ? `în ${companyLabel ?? 'firma aleasă'}` : 'ca categorii comune'}.
          Codul rămâne cel din fișier; denumirea și tipul le poți schimba aici.
        </p>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-3 max-h-80 space-y-2 overflow-y-auto">
          {items.map((item, i) => (
            <div key={item.code} className="flex items-center gap-2">
              <span className="w-40 shrink-0 truncate font-mono text-xs text-gray-600">
                {item.code}
              </span>
              <input
                type="text"
                aria-label={`Denumirea categoriei ${item.code}`}
                value={item.name}
                onChange={(e) =>
                  setItems((prev) =>
                    prev.map((p, j) => (j === i ? { ...p, name: e.target.value } : p)),
                  )
                }
                className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
              />
              <select
                aria-label={`Tipul categoriei ${item.code}`}
                value={item.kind}
                onChange={(e) =>
                  setItems((prev) =>
                    prev.map((p, j) =>
                      j === i ? { ...p, kind: e.target.value as CategoryKind } : p,
                    ),
                  )
                }
                className="rounded border border-gray-300 px-2 py-1 text-xs"
              >
                <option value="EXPENSE">Cheltuială</option>
                <option value="INCOME">Venit</option>
              </select>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Renunță
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'Se creează…' : `Creează ${items.length} și verifică din nou`}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Result step ------------------------------------------------------------

function ResultStep({ result, entityNoun }: { result: ConfirmResult; entityNoun: string }) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-emerald-400">
          <CheckCircle2 className="h-4 w-4" />
          Import finalizat
        </p>
        <div
          className={`mt-3 grid grid-cols-2 gap-2 ${result.revived > 0 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`}
        >
          <Stat label={`${entityNoun} creați`} value={result.created} tone="green" />
          {/* Rows matching a previously deleted record are reactivated, not
              re-inserted — shown only when it happened, to avoid explaining
              soft deletes on every import. */}
          {result.revived > 0 && (
            <Stat label="Reactivați (existau șterși)" value={result.revived} tone="blue" />
          )}
          <Stat label="Omise (duplicate)" value={result.skipped} tone="amber" />
          <Stat label="Eșuate" value={result.failed.length} tone="red" />
        </div>
      </div>

      {result.failed.length > 0 && (
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-sm font-semibold text-red-700">
            <XCircle className="h-4 w-4" />
            {result.failed.length}{' '}
            {result.failed.length === 1
              ? 'rând nu a putut fi importat'
              : 'rânduri nu au putut fi importate'}
          </p>
          <div className="max-h-60 overflow-auto rounded-md border border-red-500/30">
            <table className="min-w-full divide-y divide-red-500/20 text-sm">
              <thead className="bg-red-500/10 text-left text-xs font-semibold uppercase tracking-wide text-red-600">
                <tr>
                  <th className="w-16 px-3 py-2">Rând</th>
                  <th className="px-3 py-2">Eroare</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-500/20 bg-white">
                {result.failed.map((f) => (
                  <tr key={f.rowNumber} className="text-red-600">
                    <td className="px-3 py-2">{f.rowNumber}</td>
                    <td className="px-3 py-2">{f.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'green' | 'blue' | 'amber' | 'red';
}) {
  const toneClass = {
    green: 'text-green-700',
    blue: 'text-blue-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
  }[tone];
  return (
    <div className="rounded border border-gray-200 bg-white px-3 py-2 text-center">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-lg font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

// --- Utils ------------------------------------------------------------------

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}
